import React, { useState, useEffect } from 'react';
import { ModalCloseIcon } from '../../../../utils/svg';
import {
  fetchPredefinedWhatsAppTemplates,
  PredefinedTemplate
} from '../sms_window/predefinedWhatsappMessage';

type Props = {
  onClose: () => void;
  studentUUID: string;
  onSelect: (selectedMessage: string) => void;
  onSend: (tag: string) => void;
};

type MessageProps = {
  children: React.ReactNode;
  onClick?: () => void;
  selected: boolean;
};

const Messages = ({ children, onClick, selected }: MessageProps) => {
  return (
    <div
      onClick={onClick}
      className={`border border-dashed p-3 rounded-b-[10px] rounded-tl-[10px] cursor-pointer hover:bg-[#fdb58412] hover:border-[#FDB584] ${selected ? 'bg-[#fdb58412] border-[#FDB584]' : 'border-[#8A8A9A]'}`}
    >
      <div
        className={`text-[13px] font-normal leading-normal tracking-[0.267px] hover:text-[#EE731B] ${selected ? 'text-[#EE731B]' : 'text-[#8A8A9A]'}`}
      >
        {children}
      </div>
    </div>
  );
};

const PredefinedMessage = ({ onClose, studentUUID, onSelect, onSend }: Props) => {
  const [templates, setTemplates] = useState<PredefinedTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetchPredefinedWhatsAppTemplates(studentUUID)
      .then((result) => {
        setTemplates(result);
        setSelectedId(result[0].id);
        onSelect(result[0].tag);
      })
      .catch((err) => console.error('Failed to fetch templates:', err))
      .finally(() => setLoading(false));
  }, [studentUUID]);

  const handleSelectTemplate = (id: number, tag: string) => {
    setSelectedId(id);
    onSelect(tag);
  };

  return (
    <div className="bg-[var(--overlay-modal)] absolute inset-0 z-50 w-full flex items-end">
      <div className="bg-[var(--surface-card)] w-full rounded-t-[15px] pt-[15px] pr-[15px] pl-[25px] pb-[30px]">
        <div className="flex justify-between">
          <div></div>
          <div className="flex items-end justify-end cursor-pointer" onClick={onClose}>
            <ModalCloseIcon />
          </div>
        </div>
        <div className="text-[var(--text-primary)] text-sm font-semibold leading-[22px] mb-4">
          Select your message
        </div>
        <div className="flex flex-col overflow-y-auto gap-3.5 max-h-[300px]">
          {loading
            ? Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse border border-dashed border-[#8A8A9A] p-3 rounded-b-[10px] rounded-tl-[10px]"
                >
                  <div className="h-4 bg-gray-300 rounded mb-2 w-3/4"></div>
                  <div className="h-4 bg-gray-300 rounded mb-2 w-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                </div>
              ))
            : templates.map((template) => (
                <Messages
                  key={template.id}
                  onClick={() => handleSelectTemplate(template.id, template.tag)}
                  selected={selectedId === template.id}
                >
                  {template.sms_body.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                  {(template.tag === 'care_predefined_message_template2' ||
                    template.tag === 'predefined_message_for_care') && (
                    <div className="mt-3 flex flex-col gap-3">
                      {[`I'm available now`, `I'm available later`].map((label) => (
                        <button
                          key={label}
                          type="button"
                          disabled
                          className="w-full border border-[#8A8A9A] rounded-[10px] py-0.5 text-[14px] text-[#1C2430] font-semibold opacity-50 cursor-not-allowed bg-white"
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                  {template.tag === 'care_to_members_new_3' && (
                    <div className="mt-3 flex flex-col gap-3">
                      {['Great', 'Okay', 'Needs improvement'].map((label) => (
                        <button
                          key={label}
                          type="button"
                          disabled
                          className="w-full border border-[#8A8A9A] rounded-[10px] py-0.5 text-[14px] text-[#1C2430] font-semibold opacity-50 cursor-not-allowed bg-white"
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </Messages>
              ))}
        </div>
        <button
          type="button"
          disabled={loading || selectedId === null}
          onClick={() => {
            const tag = templates.find((t) => t.id === selectedId)?.tag;
            if (tag) onSend(tag);
          }}
          className="mt-4 w-full rounded-[10px] bg-[var(--state-success)] py-2.5 text-[14px] font-semibold text-[var(--text-inverse)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Send WhatsApp message
        </button>
      </div>
    </div>
  );
};

export default PredefinedMessage;
